import { ChangeDetectionStrategy, Component, OnInit, inject, input, output, signal } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { CopomexService } from '../../../core/services/copomex.service';
import { ICompanyAddress } from '../../../core/models/company.model';

/** Por ahora el país siempre es México (COMPANIES.country NOT NULL DEFAULT 'MX'). */
export const DEFAULT_COUNTRY = 'MX';
export const COUNTRIES = [
  { code: 'MX', name: 'México' },
];

/** Formulario de dirección (todos los campos opcionales, regla 38). */
export function buildAddressForm(fb: FormBuilder, a?: ICompanyAddress | null): FormGroup {
  return fb.group({
    zipCode: [a?.zipCode ?? ''],
    neighborhood: [a?.neighborhood ?? null],
    street: [a?.street ?? ''],
    numberOutside: [a?.numberOutside ?? ''],
    numberInside: [a?.numberInside ?? ''],
    building: [a?.building ?? ''],
    municipality: [a?.municipality ?? ''],
    state: [a?.state ?? ''],
    city: [a?.city ?? ''],
    // nonNullable: reset() regresa al valor inicial (MX), nunca a null.
    country: fb.nonNullable.control(a?.country || DEFAULT_COUNTRY),
    latitude: [a?.latitude ?? null],
    longitude: [a?.longitude ?? null],
  });
}

/** Valor para la API: cadenas vacías → null; lat/lng juntos o ambos null. */
export function addressValue(form: FormGroup): ICompanyAddress {
  const v = form.getRawValue();
  const s = (x: unknown) => (typeof x === 'string' && x.trim() ? x.trim() : null);
  const hasPin = typeof v.latitude === 'number' && typeof v.longitude === 'number';
  return {
    zipCode: s(v.zipCode), neighborhood: s(v.neighborhood), street: s(v.street), numberOutside: s(v.numberOutside),
    numberInside: s(v.numberInside), building: s(v.building), municipality: s(v.municipality), state: s(v.state),
    city: s(v.city), country: s(v.country) ?? DEFAULT_COUNTRY, latitude: hasPin ? v.latitude : null, longitude: hasPin ? v.longitude : null,
  };
}

export function hasAddress(a: ICompanyAddress | null | undefined): boolean {
  // El país siempre trae MX: no cuenta como dirección capturada.
  return !!a && Object.entries(a).some(([k, v]) => k !== 'country' && v !== null && v !== undefined && v !== '');
}

export function addressLines(a: ICompanyAddress | null | undefined): string[] {
  if (!a) return [];
  const l1 = [a.street, a.numberOutside].filter(Boolean).join(' ') + (a.numberInside ? `, ${a.numberInside}` : '');
  const l2 = [a.neighborhood, a.zipCode, a.city].filter(Boolean).join(', ');
  const country = COUNTRIES.find(c => c.code === a.country)?.name ?? a.country ?? '';
  return [l1, l2, country].filter(x => !!x && x.trim());
}

/** Campos de dirección con autocompletado de colonia y ciudad por código postal (Copomex). */
@Component({
  selector: 'app-location-fields',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="group" [formGroup]="form()">
      <p class="cr-eyebrow cr-text-tertiary">Dirección</p>
      <div class="cr-field">
        <label class="cr-field__label" for="zip">Código postal</label>
        <input id="zip" class="cr-input" formControlName="zipCode" inputmode="numeric" maxlength="5" placeholder="Ej. 06700"
          [class.is-invalid]="zipError()" (input)="onZip($any($event.target).value)" />
        @if (zipError()) { <p class="cr-field__error">{{ zipError() }}</p> }
        @else { <p class="cr-field__helper">Completa colonia y ciudad.</p> }
      </div>
      <div class="cr-field">
        <label class="cr-field__label" for="neighborhood">Colonia</label>
        @if (neighborhoods().length) {
          <ng-select labelForId="neighborhood" formControlName="neighborhood" [items]="neighborhoods()" placeholder="Elige tu colonia" />
        } @else {
          <input id="neighborhood" class="cr-input" formControlName="neighborhood" placeholder="Ej. Roma Norte" />
        }
      </div>
      <div class="cr-field">
        <label class="cr-field__label" for="street">Calle</label>
        <input id="street" class="cr-input" formControlName="street" placeholder="Ej. Colima" />
      </div>
      <div class="cr-field">
        <label class="cr-field__label" for="numberOutside">Número exterior</label>
        <input id="numberOutside" class="cr-input" formControlName="numberOutside" placeholder="Ej. 218" maxlength="20" />
      </div>
      <div class="cr-field">
        <label class="cr-field__label" for="numberInside">Número interior</label>
        <input id="numberInside" class="cr-input" formControlName="numberInside" placeholder="Ej. Local 3" maxlength="20" />
      </div>
      <div class="cr-field">
        <label class="cr-field__label" for="building">Edificio o plaza</label>
        <input id="building" class="cr-input" formControlName="building" placeholder="Ej. Plaza Colima" />
      </div>
    </div>
    <div class="group" [formGroup]="form()">
      <p class="cr-eyebrow cr-text-tertiary">Región</p>
      <div class="cr-field">
        <label class="cr-field__label" for="city">Ciudad</label>
        <input id="city" class="cr-input" formControlName="city" placeholder="Ej. Ciudad de México" />
      </div>
      <div class="cr-field">
        <label class="cr-field__label" for="country">País</label>
        <ng-select labelForId="country" formControlName="country" [items]="countries" bindValue="code" bindLabel="name"
          [clearable]="false" [searchable]="false" />
      </div>
    </div>
  `,
  styles: [`
    :host{display:flex;flex-direction:column;gap:28px}
    .group{display:flex;flex-direction:column;gap:20px}
    .group > p{margin:0}
  `],
})
export class LocationFieldsComponent implements OnInit {
  private copomex = inject(CopomexService);
  readonly form = input.required<FormGroup>();
  readonly detected = output<void>();
  readonly neighborhoods = signal<string[]>([]);
  readonly zipError = signal<string | null>(null);
  readonly countries = COUNTRIES;

  ngOnInit(): void {
    const zip = this.form().value.zipCode;
    if (zip && String(zip).length === 5) this.lookup(String(zip), false);
  }

  onZip(value: string): void {
    const digits = value.replace(/\D/g, '').slice(0, 5);
    if (digits !== value) this.form().patchValue({ zipCode: digits });
    this.zipError.set(null);
    if (digits.length === 5) this.lookup(digits, true);
    else this.neighborhoods.set([]);
  }

  private async lookup(cp: string, fill: boolean): Promise<void> {
    const list = await this.copomex.neighborhoods(cp);
    if (list === null) return; // servicio no disponible: se captura a mano
    if (!list.length) { this.zipError.set('Código postal no encontrado'); this.neighborhoods.set([]); return; }
    const names = [...new Set(list.map(n => n.neighborhood))].sort((a, b) => a.localeCompare(b, 'es'));
    const current = this.form().value.neighborhood;
    this.neighborhoods.set(current && !names.includes(current) ? [current, ...names] : names);
    if (!fill) return;
    const first = list[0];
    this.form().patchValue({
      neighborhood: names.length === 1 ? names[0] : (names.includes(current) ? current : null),
      city: first.city || first.municipality,
      municipality: first.municipality,
      state: first.state,
      country: DEFAULT_COUNTRY,
    });
    this.detected.emit();
  }
}
